import {z} from 'zod';
import dedent from 'dedent';

import {defineToolRegistryManifest} from '../../../types/base-tool-types';
import {ANY_RESOURCE_ID} from '@packages/domains/identity-and-access/role-based-access/data-types';

const toolName = 'seo-audit-site-page-settings' as const;

export const pageSettingsAuditItemSchema = z.object({
  pageId: z.string(),
  slug: z.string(),
  hasSeoTitle: z.boolean(),
  hasSeoDesc: z.boolean(),
  hasJsonLdSchema: z.boolean(),
  isSeoTitleTooLong: z
    .boolean()
    .describe('Whether the SEO title exceeds 60 characters'),
  isSeoDescTooLong: z
    .boolean()
    .describe('Whether the SEO description exceeds 160 characters'),
  issueCount: z
    .number()
    .describe(
      'Total number of SEO issues found (missing fields + length violations)'
    ),
  seoTitle: z.string().nullable(),
  seoDesc: z.string().nullable(),
  lastUpdated: z.date().nullable().optional(),
  isDynamic: z
    .boolean()
    .optional()
    .describe('Whether this page is a CMS collection template (dynamic page)'),
});

export type PageSettingsAuditItem = z.infer<typeof pageSettingsAuditItemSchema>;

export const auditSitePageSettingsArgsSchema = z.object({
  siteId: z.string().describe('The ID of the site to audit'),
  pageIds: z
    .array(z.string())
    .optional()
    .describe(
      'Optional list of specific page IDs to audit. If not provided, audits all pages.'
    ),
});

export type AuditSitePageSettingsArgs = z.infer<
  typeof auditSitePageSettingsArgsSchema
>;

export const auditSitePageSettingsResultSchema = z.object({
  siteId: z.string(),
  pagesAudited: z.number().describe('Total number of pages audited'),
  pagesWithIssues: z
    .number()
    .describe(
      'Number of pages with at least one SEO issue (missing or invalid field)'
    ),
  pages: z
    .array(pageSettingsAuditItemSchema)
    .describe('Audit results for pages that have at least one issue'),
});

export type AuditSitePageSettingsResult = z.infer<
  typeof auditSitePageSettingsResultSchema
>;

export const auditSitePageSettingsManifest = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Audit site page SEO settings',
  prettyStatus: {
    executed: 'Audited site pages for SEO settings',
    executing: 'Auditing site pages for SEO settings',
    failed: 'Failed to audit site page SEO settings',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Audits all pages across a site (or specified pages) and identifies those
      with SEO issues: missing meta title, meta description, or JSON-LD schema,
      as well as meta titles exceeding 60 characters or meta descriptions
      exceeding 160 characters.

      Returns a list of pages that have at least one issue, along with
      aggregate statistics. Use this before calling generate-page-settings to
      get the list of pages that need metadata generated or shortened.
    `,
    inputSchema: auditSitePageSettingsArgsSchema,
  },
  type: 'server',
  permission: [
    {
      resource: 'SitePageSettings',
      action: 'read',
      resolveResources: (args) =>
        args.pageIds?.length
          ? args.pageIds.map((id) => ({type: 'SitePageSettings' as const, id}))
          : [{type: 'SitePageSettings' as const, id: ANY_RESOURCE_ID}],
    },
  ],
  confirmBeforeExecuting: false,
  timeout: 300000,
});
